<script setup lang="ts">
import { ref, computed } from "vue";

import FloorplanSelect from "./FloorplanSelect.vue";
import MaterialsPanel from "./MaterialsPanel.vue";
import ProductSelect from "./ProductSelect.vue";

import type { Configurator } from "../libs/configurator/Configurator";

const props = defineProps<{
    cfg: Configurator;
    productOptions: { value: string; title: string; codes: string[] }[];
    floorplanOptions: { value: string; label: string; chairs: number }[];
}>();

const emit = defineEmits<{
    (e: "onChange"): void;
    (e: "onScreenshot"): void;
}>();

// Sticky filters
const productFilter = ref("");
const duoOnly = ref(false);

// Shortcuts
const primaryProduct = computed({
    get: () => props.cfg.primary.parts.length ? JSON.stringify(props.cfg.primary.parts.map((p) => p.mastercode)) : null,
    set: (v: string | null) => {
        if (v) { props.cfg.setPrimaryFromMastercodes(v, props.cfg.primary.parts[0]?.extended ?? false); }
        emit("onChange");
    },
});
const secondaryProduct = computed({
    get: () => props.cfg.secondary?.parts.length
        ? JSON.stringify(props.cfg.secondary.parts.map((p) => p.mastercode))
        : null,
    set: (v: string | null) => {
        if (v) { props.cfg.setChairTemplateFromMastercodes(v, props.cfg.secondary?.parts[0]?.extended ?? false); }
        emit("onChange");
    },
});

// Handlers
function onFloorplanChange(id: string) {
    props.cfg.setFloorplan(id);
    emit("onChange");
}
function onPrimaryProductChange(jsonList: string) {
    props.cfg.setPrimaryFromMastercodes(jsonList, false);
    emit("onChange");
}
function onChairProductChange(jsonList: string) {
    props.cfg.setChairTemplateFromMastercodes(jsonList, false);
    emit("onChange");
}
</script>

<template>
    <section>
        <hr>
        <section style="width:25vw; min-width:300px; max-width:700px; display: flex; justify-content: space-around; align-items: center;">
            <label style="font-weight: bold; font-size: 20px;">Floorplan: </label>
            <FloorplanSelect
                :model-value="cfg.currentFloorplanId"
                :floorplan-options="floorplanOptions"
                @update:model-value="(value) => onFloorplanChange(value)"
            />
        </section>
        <hr>
        <section>
            <label style="font-weight: bold; font-size: 20px; padding-bottom: 1px;">Products</label>

            <div style="display:grid; grid-template-columns: 1fr auto auto; gap:8px; align-items:center; margin-bottom:8px;">
                <input
                    v-model="productFilter"
                    type="text"
                    placeholder="Prefilter products…"
                    style="padding:6px 8px; border:1px solid #d0d5dd; border-radius:6px;"
                >
                <button
                    type="button"
                    style="padding:6px 8px; border:1px solid #d0d5dd; border-radius:6px;"
                    @click="() => productFilter = ''"
                >
                    Clear
                </button>
                <label style="display:flex; align-items:center; gap:6px;">
                    <input
                        v-model="duoOnly"
                        type="checkbox"
                    >
                    Only duo-mastercodes
                </label>
            </div>
            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
                <label style="display:block; font-weight:600; margin-bottom:4px;">Product #1</label>
                <div style="flex: 0 0 40vw; max-width: 40vw; min-width: 250px;">
                    <ProductSelect
                        v-model="primaryProduct"
                        :extended="cfg.primary.parts[0]?.extended ?? false"
                        :options="productOptions"
                        :external-filter="productFilter"
                        :duo-only="duoOnly"
                        placeholder="Search 6000+ products…"
                        @update:modelValue="() => $emit('onChange')"
                        @update:extended="(ext: boolean) => {
                            cfg.setExtended(0, ext); $emit('onChange')
                        }"
                    />
                </div>
                <div style="flex: 1;">
                    <MaterialsPanel
                        :cfg="cfg"
                        :product-id="0"
                        @on-change="() => $emit('onChange')"
                    />
                </div>
            </div>
            
            <div
                v-if="cfg.hasChairs"
                style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;"
            >
                <label style="display:block; font-weight:600; margin-bottom:4px;">Product #2</label>
                <div style="flex: 0 0 40vw; max-width: 40vw; min-width: 250px;">
                    <ProductSelect
                        v-model="secondaryProduct"
                        :extended="cfg.secondary?.parts[0]?.extended ?? false"
                        :options="productOptions"
                        :external-filter="productFilter"
                        :duo-only="duoOnly"
                        placeholder="Search products…"
                        @update:model-value="() => $emit('onChange')"
                        @update:extended="(ext: boolean) => {
                            cfg.setExtended(1, ext); $emit('onChange')
                        }"
                    />
                </div>
                <div style="flex: 1;">
                    <MaterialsPanel
                        :cfg="cfg"
                        :product-id="1"
                        @on-change="() => $emit('onChange')"
                    />
                </div>
            </div>

            <div v-if="cfg.hasChairs">
                <label style="display:block; font-weight:600; margin-bottom:4px;">Product #2 (Chair template)</label>
                <ProductSelect
                    v-model="secondaryProduct"
                    :extended="cfg.secondary?.parts[0]?.extended ?? false"
                    :options="productOptions"
                    :external-filter="productFilter"
                    :duo-only="duoOnly"
                    placeholder="Search products…"
                    @update:model-value="() => $emit('onChange')"
                />
            </div>
        </section>

        <hr>
        <button
            type="button"
            @click="() => $emit('onScreenshot')"
        >
            Télécharger l'image (3000x3000)
        </button>
    </section>
</template>
